import { Role } from '@/types/Role'
import { Daycare } from '@/types/Daycare'
import { FunctionComponent, JSX, memo, NamedExoticComponent, useState } from 'react'
import styles from './newsDraftFeed.module.scss'
import Title from '@/components/_custom/Title/title'
import { Post } from '@/types/NewsFeed'
import PostDialog from '../newsFeed/PostRow/Dialog/postDialog'
import Loading from '@/components/Loading/Loading'
import ErrorMessage from '@/components/_custom/ErrorMessage/ErrorMessage'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { useTranslation } from 'react-i18next'
import { MoreHoriz } from '@mui/icons-material'
import PostDraftRow, { PostDraftRowProps } from './PostDraftRow'
import { useFeedData } from '@/hooks/useFeedData'

interface NewsDraftFeedProps {
    role: Role
    daycare: Daycare
    canUpdate: boolean
    canValidPosts: boolean
    isApplication: boolean
}

const PostRowMemo: NamedExoticComponent<PostDraftRowProps> = memo(PostDraftRow, (prevProps, nextProps) => {
    return (
        prevProps.post.id === nextProps.post.id &&
        prevProps.post.creationDate === nextProps.post.creationDate &&
        prevProps.role === nextProps.role &&
        prevProps.daycare === nextProps.daycare &&
        prevProps.canUpdate === nextProps.canUpdate &&
        prevProps.isApplication === nextProps.isApplication &&
        prevProps.canValidPosts === nextProps.canValidPosts
    )
})

const NewsDraftFeed: FunctionComponent<NewsDraftFeedProps> = ({
    role,
    daycare,
    canUpdate,
    canValidPosts,
    isApplication
}: NewsDraftFeedProps) => {
    const { t } = useTranslation()

    const [openDialogAdd, setOpenDialogAdd] = useState(false)

    const { sortedPosts, hasMorePosts, isLoading, isLoadingMore, isRefetching, error, hasData, loadMore, refetch } =
        useFeedData({
            daycareId: daycare.id,
            role,
            isDraft: true,
            categoryIds: []
        })

    return (
        <div className={styles.NewsFeedPlacement}>
            <div className={styles.Form}>
                <div className={`${styles.TitleAndButton} ${canUpdate && role !== Role.parent && styles.MarginBottom}`}>
                    <Title text={`${t('draft')} - ${t('newsFeed')}`} className={styles.Title} />
                    {canUpdate && <ButtonTextIcon type={'add'} onClick={() => setOpenDialogAdd(true)} />}
                </div>

                {(isLoading || isRefetching) && <Loading overall={false} />}

                {hasData && (
                    <div className={styles.Container}>
                        {sortedPosts.length === 0 ? (
                            <p className={styles.NoPostsText}>{t('noPostsInDrafting')}</p>
                        ) : (
                            sortedPosts.map(
                                (post: Post): JSX.Element => (
                                    <PostRowMemo
                                        key={post.id}
                                        role={role}
                                        post={post}
                                        daycare={daycare}
                                        refetch={refetch}
                                        canUpdate={canUpdate}
                                        isApplication={isApplication}
                                        canValidPosts={canValidPosts}
                                    />
                                )
                            )
                        )}

                        {isLoadingMore && <Loading overall={false} />}

                        {sortedPosts.length > 1 && hasMorePosts && (
                            <div className={styles.ButtonContainer}>
                                <ButtonTextIcon
                                    type={'custom'}
                                    className={styles.ShowMoreButton}
                                    customText={t('showMore')}
                                    customIcon={<MoreHoriz />}
                                    onClick={loadMore}
                                    parent={role === Role.parent}
                                />
                            </div>
                        )}

                        {openDialogAdd && (
                            <PostDialog
                                open={openDialogAdd}
                                handleClose={() => setOpenDialogAdd(false)}
                                role={role}
                                daycare={daycare}
                                modifyPost={null}
                                refetch={refetch}
                            />
                        )}
                    </div>
                )}
            </div>

            {error && <ErrorMessage error={t('generalError')} />}
        </div>
    )
}

export default NewsDraftFeed
